import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { reviewSites, reviewDimensions } from "@/mocks/reviews";
import BackToTop from "@/components/feature/BackToTop";

type SortKey = "rating" | "ads" | "speed";

const tabs = [
  { key: "rating", label: "综合评分" },
  { key: "ads", label: "广告控制" },
  { key: "speed", label: "加载速度" },
];

function rankColor(i: number) {
  if (i === 0) return "bg-secondary-500 text-background-50";
  if (i === 1) return "bg-foreground-400 text-background-50";
  if (i === 2) return "bg-secondary-300 text-foreground-950";
  return "bg-background-200 text-foreground-700";
}

export default function Rankings() {
  const [sortKey, setSortKey] = useState<SortKey>("rating");

  const sorted = [...reviewSites].sort((a, b) => {
    if (sortKey === "rating") return b.rating - a.rating;
    return b[sortKey].score - a[sortKey].score;
  });

  const dimension = reviewDimensions.find((d) => d.key === sortKey);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">综合榜单</span>
        </nav>

        <div className="mt-6 max-w-2xl">
          <h1 className="font-display text-3xl text-foreground-950 md:text-4xl">漫画网站综合榜单</h1>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            基于广告控制、加载速度、内容收录与良心程度四个维度综合评分。你可以切换排序方式，从不同角度筛选适合自己的漫画网站。
          </p>
        </div>

        {/* tabs */}
        <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setSortKey(t.key as SortKey)}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm transition-colors ${
                sortKey === t.key ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {dimension && sortKey !== "rating" && (
          <p className="mt-4 text-sm text-foreground-500">当前维度：{dimension.label} · {dimension.desc}</p>
        )}

        {/* ranking list */}
        <div className="mt-8 space-y-4">
          {sorted.map((site, i) => (
            <Link
              key={site.slug}
              to={`/reviews/${site.slug}`}
              className="group flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:flex-row md:items-center"
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-lg ${rankColor(i)}`}>
                {i + 1}
              </span>
              <img
                src={site.img}
                alt={`${site.name}漫画网站榜单缩略图`}
                title={`${site.name} 漫画网站评测`}
                className="h-20 w-full shrink-0 rounded-lg object-cover sm:w-32"
              />
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-display text-lg text-foreground-950">{site.name}</h2>
                  {site.badge && (
                    <span className={`rounded-full px-2 py-0.5 text-xs ${site.badge === "避雷警示" ? "bg-red-500 text-background-50" : "bg-secondary-100 text-secondary-900"}`}>
                      {site.badge}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-foreground-600">{site.summary}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                  <span className="flex items-center gap-1"><i className="ri-advertisement-line text-secondary-600"></i>广告 {site.ads.score}</span>
                  <span className="flex items-center gap-1"><i className="ri-speed-line text-accent-600"></i>加载 {site.speed.score}</span>
                  <span className="flex items-center gap-1"><i className="ri-heart-line text-primary-500"></i>良心 {site.sincere.score}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 md:flex-col md:items-end">
                <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-sm font-semibold text-accent-900">
                  <i className="ri-star-fill text-accent-600"></i>
                  {sortKey === "rating" ? site.rating : `${site[sortKey].score}.0`}
                </span>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 opacity-0 transition-opacity group-hover:opacity-100">
                  详情 <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}